import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import { ApiError } from '@/api/client';
import { useLogin } from '@/api/queries/auth';
import { Button, Field } from '@pixlide/ui';
import { AuthLayout, FormAlert } from '@/features/auth/AuthLayout';
import { SocialAuthButtons } from '@/features/auth/SocialAuthButtons';
import { TwoFactorChallengeStep } from '@/features/auth/TwoFactorChallengeStep';
import { forgetLastAccount, readLastAccount, rememberLastAccount } from '@/features/auth/last-account';
import { loginSchema, type LoginInput } from '@/features/auth/schemas';

/**
 * Messages d'erreur métier, indexés par le CODE stable renvoyé par l'API.
 *
 * On ne teste jamais le message du serveur : il peut être reformulé ou traduit sans
 * préavis, alors que le code fait partie du contrat. Cette table permet aussi d'ajouter
 * une action contextuelle (« renvoyer l'email de confirmation ») là où le serveur ne peut
 * fournir qu'un texte.
 */
const ERROR_MESSAGES: Record<string, string> = {
  'auth.invalid_credentials': 'Adresse email ou mot de passe incorrect.',
  'auth.email_not_confirmed':
    "Votre adresse email n'est pas encore confirmée. Consultez le message que nous vous avons envoyé.",
  'auth.account_locked':
    'Votre compte est temporairement bloqué après plusieurs tentatives. Réessayez dans quelques minutes.',
  'auth.account_disabled': 'Ce compte a été désactivé. Contactez le propriétaire du compte.',
  'auth.external_provider_required':
    'Ce compte se connecte via Google ou LinkedIn. Utilisez le bouton correspondant.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();

  // Non `null` uniquement le temps du second facteur : le compte est déjà authentifié par
  // mot de passe, il ne manque plus que le code. Porter ce jeton dans l'état plutôt que
  // dans l'URL évite qu'il traîne dans l'historique de navigation.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);

  // Lecture UNE SEULE FOIS au montage (initialiseur paresseux) : relire le stockage à
  // chaque rendu ferait réapparaître la pastille juste après un clic sur « Changer de
  // compte ».
  const [knownEmail, setKnownEmail] = useState(readLastAccount);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: knownEmail ?? '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const result = await login.mutateAsync(values);
    rememberLastAccount(values.email);

    if (result.twoFactorRequired && result.twoFactorChallengeToken) {
      setChallengeToken(result.twoFactorChallengeToken);

      return;
    }

    await navigate({ to: '/' });
  });

  if (challengeToken !== null) {
    return <TwoFactorChallengeStep challengeToken={challengeToken} onCancel={() => setChallengeToken(null)} />;
  }

  function switchAccount() {
    forgetLastAccount();
    setKnownEmail(null);
    setValue('email', '');

    // Le champ réapparaît vide : y placer le focus évite un clic pour une action que
    // l'utilisateur vient précisément de demander.
    setFocus('email');
  }

  const apiError = login.error instanceof ApiError ? login.error : null;

  const alertMessage =
    apiError === null
      ? null
      : (ERROR_MESSAGES[apiError.code] ?? apiError.message);

  // Une adresse non confirmée n'est pas une impasse : le compte existe, il lui manque un
  // code. L'alerte porte donc le chemin de sortie, plutôt que de laisser la personne
  // deviner qu'un écran de confirmation existe encore quelque part.
  const alertAction =
    apiError?.code === 'auth.email_not_confirmed' ? (
      <Link
        to="/confirmation-email"
        search={{ email: getValues('email') }}
        className="font-semibold underline"
      >
        Saisir le code reçu par email
      </Link>
    ) : null;

  return (
    <AuthLayout
      tab="login"
      title="Se connecter"
      subtitle="Saisissez vos informations ci-dessous pour vous connecter."
    >
      {alertMessage ? <FormAlert message={alertMessage} action={alertAction} /> : null}

      {/* `noValidate` : la validation native du navigateur est désactivée au profit de Zod,
          qui produit des messages en français cohérents avec ceux du serveur. */}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        {knownEmail === null ? (
          <Field
            label="Adresse email"
            type="email"
            autoComplete="email"
            placeholder="monadresse@mail.com"
            // `autoFocus` sur le premier champ d'un écran de connexion : l'utilisateur arrive
            // ici pour taper, pas pour cliquer.
            autoFocus
            error={errors.email?.message}
            {...register('email')}
          />
        ) : (
          <>
            {/* L'adresse reste dans le formulaire, donc dans la requête, même quand elle
                n'est plus saisissable : la pastille ci-dessous n'est qu'un affichage. */}
            <input type="hidden" {...register('email')} />
            <KnownAccount email={knownEmail} onChange={switchAccount} />
          </>
        )}

        <Field
          label="Mot de passe"
          type="password"
          revealable
          placeholder="••••••••••"
          // `current-password` (et non `new-password`) : indique aux gestionnaires de mots
          // de passe qu'il s'agit d'une connexion, pas d'une création.
          autoComplete="current-password"
          autoFocus={knownEmail !== null}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between gap-4 text-[11px]">
          {knownEmail === null ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={switchAccount}
              className="text-ink-500 transition hover:text-ink-900"
            >
              Utiliser un autre compte
            </button>
          )}

          <Link
            to="/mot-de-passe-oublie"
            className="text-ink-500 transition hover:text-ink-900"
          >
            Mot de passe oublié ?
          </Link>
        </div>

        <Button
          type="submit"
          isLoading={isSubmitting || login.isPending}
          className="mt-2 h-11 w-full rounded-xl"
        >
          Se connecter
          <ArrowRight aria-hidden className="size-4" />
        </Button>
      </form>

      <p className="mt-4 text-[11px] text-ink-500">
        Vous n'avez pas encore de compte ?{' '}
        <Link to="/inscription" className="font-semibold text-ink-900 hover:underline">
          Inscrivez-vous gratuitement
        </Link>
      </p>

      <div className="mt-5">
        <SocialAuthButtons />
      </div>
    </AuthLayout>
  );
}

/** Rappel du dernier compte utilisé, avec sa sortie de secours. */
function KnownAccount({ email, onChange }: { email: string; onChange: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 ring-1 ring-ink-100">
      <span
        aria-hidden
        className="grid size-7 shrink-0 place-items-center rounded-pill bg-brand-100 text-[10px] font-semibold text-brand-700"
      >
        {email.slice(0, 2).toUpperCase()}
      </span>

      {/* `min-w-0` + `truncate` : une adresse longue est coupée au lieu d'élargir la boîte
          et de pousser le bouton hors de l'écran. */}
      <span className="min-w-0 flex-1 truncate text-xs text-ink-500">{email}</span>

      <button
        type="button"
        onClick={onChange}
        className="shrink-0 rounded-pill px-2.5 py-1.5 text-[10px] font-medium text-ink-900 ring-1 ring-ink-100 transition hover:bg-ink-50"
      >
        Changer de compte
      </button>
    </div>
  );
}
